import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import Checkbox from 'src/components/atoms/Checkbox/Checkbox';
import {
  ContactFieldCheck,
  ReviewContact
} from 'src/features/proofs/ts/interfaces/OfferLetterVerification';
import styled from 'styled-components/macro';
import { isE164Phone, normalizePhoneToE164 } from '../helpers/contactPhone';
import {
  AddBonusButton,
  OfferLetterCheckItemButtons,
  OfferLetterCheckItemLabel,
  OfferLetterCheckItemRow,
  OfferLetterEmailRow,
  OfferLetterLlmField,
  OfferLetterLlmInput,
  OfferLetterPassFailButton,
  OfferLetterTooltipContent,
  OfferLetterTooltipIcon,
  OfferLetterTooltipWrapper,
  RemoveBonusButton
} from '../styles';

// --- Domain utilities ---

// Known limitation: two-part TLDs (.co.uk, .com.au) are not handled —
// e.g., careers.acme.co.uk extracts "co.uk" instead of "acme.co.uk".
// Acceptable for US-focused offer letter verification. A public suffix
// list approach would be needed for international support.
export function extractDomain(url: string): string | null {
  try {
    const hostname = new URL(url).hostname;
    const parts = hostname.split('.');
    return parts.length >= 2 ? parts.slice(-2).join('.') : hostname;
  } catch {
    return null;
  }
}

// ITL-2880: accepts null/undefined so a schema-legal null element can't crash `null.split`.
export function emailMatchesDomain(email: string | null | undefined, domain: string): boolean {
  const emailDomain = email?.split('@')[1]?.toLowerCase();
  if (!emailDomain) return false;
  const emailPrimary = emailDomain.split('.').slice(-2).join('.');
  return emailPrimary === domain.toLowerCase();
}

export function deriveEmailCheck(emailChecks: ContactFieldCheck[]): ContactFieldCheck {
  // No emails = unset (contact cannot be fully verified without at least one valid email)
  if (emailChecks.length === 0) return 'unset';
  if (emailChecks.some((c) => c === 'pass')) return 'pass';
  if (emailChecks.every((c) => c === 'fail')) return 'fail';
  return 'unset';
}

function initEmailChecks(
  emails: string[] | null,
  companyDomain: string | null
): ContactFieldCheck[] {
  if (!emails || emails.length === 0) return [];
  return emails.map((email) => {
    if (!companyDomain) return 'unset';
    return emailMatchesDomain(email, companyDomain) ? 'unset' : 'fail';
  });
}

// --- Props & Styled ---

interface ContactReviewSectionProps {
  contacts: ReviewContact[] | null;
  fieldValues: Record<string, string>;
  onContactsChange: (contacts: ReviewContact[]) => void;
}

const CompanyInfoBlock = styled.div`
  background: ${({ theme }) => theme.color.gray[50]};
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray[500]};
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const CompanyInfoItem = styled.span`
  display: inline-flex;
  gap: 4px;
`;

const CompanyInfoLabel = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[400]};
`;

const EmptyState = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray[300]};
  font-style: italic;
  padding: 8px 0;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.gray[500]};
  margin-bottom: 8px;
`;

const ContactCard = styled.div`
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 8px;
`;

const ContactCardTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.gray[300]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DomainWarning = styled.span`
  font-size: 10px;
  color: #991b1b;
  font-style: italic;
  margin-left: 4px;
`;

const FieldError = styled.span`
  font-size: 10px;
  color: #991b1b;
  font-style: italic;
  display: block;
  margin-top: 2px;
`;

const EmailRowLabel = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.color.gray[400]};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const FIELD_TOOLTIPS: Record<string, string> = {
  name: 'Verify contact name exists at company (Google/LinkedIn/company website).',
  type: 'Verify title matches role at company (Google/LinkedIn/company website).',
  email: 'Email @ company domain = validated. Only on offer letter = unvalidated.',
  phone: 'Independently sourced (Google/LinkedIn) = validated. Only on letter = unvalidated.'
};

// --- Sub-components ---

interface ContactFieldRowProps {
  label: string;
  fieldKey: 'name' | 'type' | 'phone';
  value: string;
  onChange: (value: string) => void;
  checkStatus: ContactFieldCheck;
  onToggleCheck: (value: ContactFieldCheck) => void;
  testIdPrefix: string;
  // ITL-2807: phone row only — normalize on blur (commit point) and show an
  // inline validity error. Per-keystroke normalization would reformat under
  // the cursor mid-type, so it is deferred to blur.
  onBlur?: (value: string) => void;
  error?: string;
}

const ContactFieldRow: FC<ContactFieldRowProps> = ({
  label,
  fieldKey,
  value,
  onChange,
  checkStatus,
  onToggleCheck,
  testIdPrefix,
  onBlur,
  error
}) => {
  const handleToggle = (target: 'pass' | 'fail') => {
    onToggleCheck(checkStatus === target ? 'unset' : target);
  };

  return (
    <OfferLetterCheckItemRow>
      <div style={{ flex: 1 }}>
        <OfferLetterCheckItemLabel>
          {label}
          <OfferLetterTooltipWrapper>
            <OfferLetterTooltipIcon type="button" aria-label={`Show ${label} criteria`}>
              ?
            </OfferLetterTooltipIcon>
            <OfferLetterTooltipContent>{FIELD_TOOLTIPS[fieldKey]}</OfferLetterTooltipContent>
          </OfferLetterTooltipWrapper>
        </OfferLetterCheckItemLabel>
        <OfferLetterLlmField>
          <OfferLetterLlmInput
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur ? (e) => onBlur(e.target.value) : undefined}
            data-testid={`${testIdPrefix}-${fieldKey}`}
          />
          {error && (
            <FieldError data-testid={`${testIdPrefix}-${fieldKey}-error`}>{error}</FieldError>
          )}
        </OfferLetterLlmField>
      </div>
      <OfferLetterCheckItemButtons alignEnd>
        <OfferLetterPassFailButton
          $variant="pass"
          $selected={checkStatus === 'pass'}
          onClick={() => handleToggle('pass')}
          aria-label={`Mark ${label} as pass`}
          data-testid={`${testIdPrefix}-${fieldKey}-pass`}
        >
          ✓
        </OfferLetterPassFailButton>
        <OfferLetterPassFailButton
          $variant="fail"
          $selected={checkStatus === 'fail'}
          onClick={() => handleToggle('fail')}
          aria-label={`Mark ${label} as fail`}
          data-testid={`${testIdPrefix}-${fieldKey}-fail`}
        >
          ✗
        </OfferLetterPassFailButton>
      </OfferLetterCheckItemButtons>
    </OfferLetterCheckItemRow>
  );
};

interface EmailRowProps {
  email: string;
  checkStatus: ContactFieldCheck;
  onToggleCheck: (value: ContactFieldCheck) => void;
  domainMismatch: boolean;
  testId: string;
}

const EmailRow: FC<EmailRowProps> = ({
  email,
  checkStatus,
  onToggleCheck,
  domainMismatch,
  testId
}) => {
  const handleToggle = (target: 'pass' | 'fail') => {
    onToggleCheck(checkStatus === target ? 'unset' : target);
  };

  return (
    <OfferLetterCheckItemRow>
      <div style={{ flex: 1 }}>
        <EmailRowLabel>
          {email}
          {domainMismatch && (
            <DomainWarning data-testid={`${testId}-domain-warn`}>domain mismatch</DomainWarning>
          )}
        </EmailRowLabel>
      </div>
      <OfferLetterCheckItemButtons>
        <OfferLetterPassFailButton
          $variant="pass"
          $selected={checkStatus === 'pass'}
          onClick={() => handleToggle('pass')}
          aria-label={`Mark email ${email} as pass`}
          data-testid={`${testId}-pass`}
        >
          ✓
        </OfferLetterPassFailButton>
        <OfferLetterPassFailButton
          $variant="fail"
          $selected={checkStatus === 'fail'}
          onClick={() => handleToggle('fail')}
          aria-label={`Mark email ${email} as fail`}
          data-testid={`${testId}-fail`}
        >
          ✗
        </OfferLetterPassFailButton>
      </OfferLetterCheckItemButtons>
    </OfferLetterCheckItemRow>
  );
};

// --- Main Component ---

const ContactReviewSection: FC<ContactReviewSectionProps> = ({
  contacts,
  fieldValues,
  onContactsChange
}) => {
  const sorted = useMemo(
    () =>
      contacts ? [...contacts].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity)) : [],
    [contacts]
  );

  const companyDomain = fieldValues['website'] ? extractDomain(fieldValues['website']) : null;

  const getOrInitChecks = useCallback(
    (contact: ReviewContact) =>
      contact.field_checks ?? {
        name: 'unset' as const,
        type: 'unset' as const,
        email: initEmailChecks(contact.email, companyDomain),
        phone: 'unset' as const
      },
    [companyDomain]
  );

  const deriveVerified = (checks: ReviewContact['field_checks']) => {
    if (!checks) return false;
    const emailDerived = deriveEmailCheck(checks.email);
    return (
      checks.name === 'pass' &&
      checks.type === 'pass' &&
      emailDerived === 'pass' &&
      checks.phone === 'pass'
    );
  };

  // Auto-initialize field_checks (including domain-based email auto-fail) on first render
  const initializedRef = useRef(false);
  const onContactsChangeRef = useRef(onContactsChange);
  onContactsChangeRef.current = onContactsChange;

  useEffect(() => {
    if (initializedRef.current || !contacts || contacts.length === 0) return;

    const needsInit = contacts.some((c) => !c.field_checks);
    if (!needsInit) {
      initializedRef.current = true;
      return;
    }

    const initialized = contacts.map((c) => {
      if (c.field_checks) return c;
      return {
        ...c,
        field_checks: {
          name: 'unset' as const,
          type: 'unset' as const,
          email: initEmailChecks(c.email, companyDomain),
          phone: 'unset' as const
        }
      };
    });
    initializedRef.current = true;
    onContactsChangeRef.current(initialized);
  }, [contacts, companyDomain]);

  const emailInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const updateContact = (idx: number, field: keyof ReviewContact, value: unknown) => {
    const contact = sorted[idx];
    const updatedContact = { ...contact, [field]: value };
    // Recalculate is_verified when an editable field is cleared
    const checks = updatedContact.field_checks;
    const updated = sorted.map((c, i) =>
      i === idx ? { ...updatedContact, is_verified: deriveVerified(checks) } : c
    );
    onContactsChange(updated);
  };

  // ITL-2807: on blur, normalize a valid phone to E.164 (mirrors the API's
  // E.164 enforcement). Blank commits as null; unparseable input is left as
  // typed so the inline error stays visible (submit is blocked by the panel
  // guard). Only commit when the value actually changes — avoids a redundant
  // onContactsChange when the phone is already in its final form.
  const normalizePhone = (idx: number, raw: string) => {
    const next = normalizePhoneToE164(raw);
    if (next !== sorted[idx].phone) {
      updateContact(idx, 'phone', next);
    }
  };

  const updateScalarFieldCheck = (
    idx: number,
    field: 'name' | 'type' | 'phone',
    value: ContactFieldCheck
  ) => {
    const contact = sorted[idx];
    const checks = getOrInitChecks(contact);
    const newChecks = { ...checks, [field]: value };
    const updated = sorted.map((c, i) =>
      i === idx ? { ...c, field_checks: newChecks, is_verified: deriveVerified(newChecks) } : c
    );
    onContactsChange(updated);
  };

  const updateEmailCheck = (contactIdx: number, emailIdx: number, value: ContactFieldCheck) => {
    const contact = sorted[contactIdx];
    const checks = getOrInitChecks(contact);
    const newEmailChecks = [...checks.email];
    newEmailChecks[emailIdx] = value;
    const newChecks = { ...checks, email: newEmailChecks };
    const updated = sorted.map((c, i) =>
      i === contactIdx
        ? { ...c, field_checks: newChecks, is_verified: deriveVerified(newChecks) }
        : c
    );
    onContactsChange(updated);
  };

  const addEmail = (contactIdx: number, email: string) => {
    if (!email.includes('@') || email.length > 254) return;
    const contact = sorted[contactIdx];
    const newEmails = [...(contact.email ?? []), email];
    const checks = getOrInitChecks(contact);
    const newEmailCheck =
      companyDomain && !emailMatchesDomain(email, companyDomain)
        ? ('fail' as const)
        : ('unset' as const);
    const newChecks = { ...checks, email: [...checks.email, newEmailCheck] };
    const updated = sorted.map((c, i) =>
      i === contactIdx ? { ...c, email: newEmails, field_checks: newChecks } : c
    );
    onContactsChange(updated);
  };

  const removeEmail = (contactIdx: number, emailIdx: number) => {
    const contact = sorted[contactIdx];
    const newEmails = (contact.email ?? []).filter((_, i) => i !== emailIdx);
    const checks = getOrInitChecks(contact);
    const newEmailChecks = checks.email.filter((_, i) => i !== emailIdx);
    const newChecks = { ...checks, email: newEmailChecks };
    const updated = sorted.map((c, i) =>
      i === contactIdx
        ? {
            ...c,
            email: newEmails,
            field_checks: newChecks,
            is_verified: deriveVerified(newChecks)
          }
        : c
    );
    onContactsChange(updated);
  };

  return (
    <div data-testid="contact-review-section">
      <SectionTitle>Contact Review</SectionTitle>

      <CompanyInfoBlock data-testid="contact-review-company-info">
        <CompanyInfoItem>
          <CompanyInfoLabel>Company:</CompanyInfoLabel>
          {fieldValues['company_name'] || '—'}
        </CompanyInfoItem>
        <CompanyInfoItem>
          <CompanyInfoLabel>Website:</CompanyInfoLabel>
          {fieldValues['website'] || '—'}
        </CompanyInfoItem>
        <CompanyInfoItem>
          <CompanyInfoLabel>Address:</CompanyInfoLabel>
          {fieldValues['address'] || '—'}
        </CompanyInfoItem>
      </CompanyInfoBlock>

      {sorted.length === 0 ? (
        <EmptyState data-testid="contact-review-empty">
          No contacts available from AI pipeline
        </EmptyState>
      ) : (
        sorted.map((contact, idx) => {
          const checks = contact.field_checks;
          const emailChecks = checks?.email ?? [];
          const testIdPrefix = `contact-review-${idx}`;

          return (
            <ContactCard key={idx} data-testid={`contact-review-row-${idx}`}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <ContactCardTitle style={{ marginBottom: 0 }}>Contact {idx + 1}</ContactCardTitle>
                <RemoveBonusButton
                  type="button"
                  onClick={() => onContactsChange(sorted.filter((_, i) => i !== idx))}
                  aria-label={`Remove contact ${idx + 1}`}
                  data-testid={`contact-review-remove-${idx}`}
                >
                  ✕
                </RemoveBonusButton>
              </div>

              <ContactFieldRow
                label="Name"
                fieldKey="name"
                value={contact.name ?? ''}
                onChange={(v) => updateContact(idx, 'name', v || null)}
                checkStatus={checks?.name ?? 'unset'}
                onToggleCheck={(v) => updateScalarFieldCheck(idx, 'name', v)}
                testIdPrefix={testIdPrefix}
              />

              <ContactFieldRow
                label="Title"
                fieldKey="type"
                value={contact.type ?? ''}
                onChange={(v) => updateContact(idx, 'type', v || null)}
                checkStatus={checks?.type ?? 'unset'}
                onToggleCheck={(v) => updateScalarFieldCheck(idx, 'type', v)}
                testIdPrefix={testIdPrefix}
              />

              <OfferLetterEmailRow>
                <OfferLetterCheckItemLabel>
                  Email
                  <OfferLetterTooltipWrapper>
                    <OfferLetterTooltipIcon type="button" aria-label="Show Email criteria">
                      ?
                    </OfferLetterTooltipIcon>
                    <OfferLetterTooltipContent>{FIELD_TOOLTIPS.email}</OfferLetterTooltipContent>
                  </OfferLetterTooltipWrapper>
                </OfferLetterCheckItemLabel>
                {(contact.email ?? []).map((email, emailIdx) => (
                  <div key={emailIdx} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ flex: 1 }}>
                      <EmailRow
                        email={email}
                        checkStatus={emailChecks[emailIdx] ?? 'unset'}
                        onToggleCheck={(v) => updateEmailCheck(idx, emailIdx, v)}
                        domainMismatch={
                          companyDomain ? !emailMatchesDomain(email, companyDomain) : false
                        }
                        testId={`${testIdPrefix}-email-${emailIdx}`}
                      />
                    </div>
                    <RemoveBonusButton
                      type="button"
                      onClick={() => removeEmail(idx, emailIdx)}
                      aria-label={`Remove email ${email}`}
                      data-testid={`${testIdPrefix}-email-${emailIdx}-remove`}
                      style={{ fontSize: 10, padding: '2px 4px' }}
                    >
                      ✕
                    </RemoveBonusButton>
                  </div>
                ))}
                <OfferLetterLlmField>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <OfferLetterLlmInput
                      ref={(el) => {
                        emailInputRefs.current[idx] = el;
                      }}
                      placeholder="Add email address..."
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const val = (e.target as HTMLInputElement).value.trim();
                          if (val) {
                            addEmail(idx, val);
                            (e.target as HTMLInputElement).value = '';
                          }
                          e.preventDefault();
                        }
                      }}
                      data-testid={`${testIdPrefix}-email-add`}
                    />
                    <AddBonusButton
                      type="button"
                      onClick={() => {
                        const input = emailInputRefs.current[idx];
                        if (!input) return;
                        const val = input.value.trim();
                        if (val) {
                          addEmail(idx, val);
                          input.value = '';
                        }
                      }}
                      style={{ padding: '4px 8px', fontSize: 12, whiteSpace: 'nowrap' }}
                      data-testid={`${testIdPrefix}-email-add-btn`}
                    >
                      + Add
                    </AddBonusButton>
                  </div>
                </OfferLetterLlmField>
              </OfferLetterEmailRow>

              <ContactFieldRow
                label="Phone"
                fieldKey="phone"
                value={contact.phone ?? ''}
                onChange={(v) => updateContact(idx, 'phone', v || null)}
                onBlur={(v) => normalizePhone(idx, v)}
                error={
                  contact.phone && !isE164Phone(contact.phone)
                    ? 'Enter a valid phone number, e.g. (415) 555-2671 or +14155552671'
                    : undefined
                }
                checkStatus={checks?.phone ?? 'unset'}
                onToggleCheck={(v) => updateScalarFieldCheck(idx, 'phone', v)}
                testIdPrefix={testIdPrefix}
              />

              <div style={{ marginTop: 8 }} data-testid={`contact-review-in-letter-${idx}`}>
                <Checkbox
                  name={`in-offer-letter-${idx}`}
                  value={`in-offer-letter-${idx}`}
                  label="In offer letter"
                  isChecked={contact.is_in_offer_letter}
                  onClick={(data) => updateContact(idx, 'is_in_offer_letter', data.isChecked)}
                />
              </div>
            </ContactCard>
          );
        })
      )}

      <AddBonusButton
        type="button"
        onClick={() => {
          const newContact: ReviewContact = {
            name: null,
            type: null,
            email: null,
            phone: null,
            source: 'analyst',
            rank: null,
            quality_score: null,
            is_in_offer_letter: false,
            is_verified: false,
            field_checks: { name: 'unset', type: 'unset', email: [], phone: 'unset' }
          };
          onContactsChange([...sorted, newContact]);
        }}
        data-testid="contact-review-add"
      >
        + Add contact
      </AddBonusButton>
    </div>
  );
};

export default ContactReviewSection;
